import tokenNames from "./tokenNames.json";

import type { TenantColors, TenantTokensFile } from "../tenants/types";

export type PrimitiveRamps = Record<string, Record<string, string>>;

export type ResolveTokensArgs = {
  primitives: PrimitiveRamps;
  baseTheme: Record<string, string>;
  tenant: TenantTokensFile;
};

const REFERENCE_PATTERN = /^\{([A-Za-z][A-Za-z0-9]*)\.([0-9]+)\}$/;

export const isReference = (value: string) => REFERENCE_PATTERN.test(value);

/**
 * Every token name the resolved map must contain: the camelCase cross-product
 * of the semantic namespaces, plus the gradient tokens.
 */
export const tokenOrder: string[] = [
  ...Object.entries(tokenNames.namespaces).flatMap(([namespace, colors]) =>
    colors.map(
      (color) =>
        `${namespace}${color
          .split("-")
          .map((part) => `${part[0].toUpperCase()}${part.slice(1)}`)
          .join("")}`,
    ),
  ),
  ...tokenNames.gradients,
];

/**
 * Merges ramp overrides per ramp and per step, without mutating either input.
 * Callers pass `require`d JSON that is cached and shared across tenants, so an
 * in-place merge would leak one tenant's ramp into the next.
 */
export const mergeRamps = (
  base: PrimitiveRamps,
  override: PrimitiveRamps = {},
): PrimitiveRamps => {
  const merged: PrimitiveRamps = {};

  for (const ramp of new Set([
    ...Object.keys(base),
    ...Object.keys(override),
  ])) {
    merged[ramp] = { ...base[ramp], ...override[ramp] };
  }

  return merged;
};

export const dereference = (
  value: string,
  ramps: PrimitiveRamps,
  tokenName: string,
): string => {
  const match = REFERENCE_PATTERN.exec(value);
  if (!match) return value;

  const [, ramp, step] = match;
  const resolved = ramps[ramp]?.[step];

  if (typeof resolved !== "string" || resolved === "") {
    throw new Error(
      `${tokenName} references ${value}, which is not a step in any primitive ramp`,
    );
  }

  return resolved;
};

/**
 * Resolves the baseTheme theme and a tenant's overrides into the flat map of
 * literal values that application code consumes. References are dereferenced
 * to their primitive value here — a `var()` chain must never reach JS, because
 * consumers such as `hexToRgba` in `client/src/containers/antdThemeConfig.ts`
 * parse these strings.
 */
export const resolveTokens = ({
  primitives,
  baseTheme,
  tenant,
}: ResolveTokensArgs): TenantColors => {
  const ramps = mergeRamps(primitives, tenant.primitives);
  const authored: Record<string, string> = { ...baseTheme, ...tenant.tokens };
  const resolved: Record<string, string> = {};

  for (const name of tokenOrder) {
    const value = authored[name];

    if (typeof value !== "string" || value === "") {
      throw new Error(`Missing color token ${name}`);
    }

    const literal = dereference(value, ramps, name);

    if (literal.includes("var(")) {
      throw new Error(
        `${name} resolved to ${literal}; application code needs a literal value, not a CSS variable`,
      );
    }

    resolved[name] = literal;
  }

  return resolved as TenantColors;
};
